import { indicationOptions } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import { Box, Typography } from "@material-ui/core";
import type { RenalWorksheetData } from "../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { EPOCUS_TEXT_SECONDARY, rem } from "../epocus-palette";
import {
  AllNormalToggle,
  BooleanToggle,
  EnumChipSelect,
  IndicationChips,
  NotesField,
  NumericInput,
  ViewChecklist,
  WorksheetColumnLeft,
  WorksheetColumnRight,
  WorksheetColumns,
  WorksheetSection,
} from "./worksheet-primitives";

const INDICATION_OPTIONS = indicationOptions("renal");

const VIEW_OPTIONS = [
  { key: "rKidneyLong", label: "R Kidney Long" },
  { key: "rKidneyTrans", label: "R Kidney Trans" },
  { key: "lKidneyLong", label: "L Kidney Long" },
  { key: "lKidneyTrans", label: "L Kidney Trans" },
  { key: "bladder", label: "Bladder" },
] as const;

const HYDRONEPHROSIS_OPTIONS = [
  { value: "none", label: "None" },
  { value: "mild", label: "Mild" },
  { value: "moderate", label: "Moderate" },
  { value: "severe", label: "Severe" },
] as const;

export const isRenalComplete = (data: RenalWorksheetData): boolean =>
  data.findings.rKidney.hydronephrosis !== null &&
  data.findings.rKidney.stones !== null &&
  data.findings.rKidney.perinephricFluid !== null &&
  data.findings.rKidney.cystOrMass !== null &&
  data.findings.lKidney.hydronephrosis !== null &&
  data.findings.lKidney.stones !== null &&
  data.findings.lKidney.perinephricFluid !== null &&
  data.findings.lKidney.cystOrMass !== null &&
  data.findings.bladderAssessed !== null;

const FindingLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 4,
    }}
  >
    {children}
  </Typography>
);

type KidneySide = "rKidney" | "lKidney";

const KidneySection: FC<{
  label: string;
  side: KidneySide;
  data: RenalWorksheetData;
  onChange: (data: RenalWorksheetData) => void;
  disabled: boolean;
}> = ({ label, side, data, onChange, disabled }) => {
  const kidney = data.findings[side];

  const updateKidney = (
    patch: Partial<RenalWorksheetData["findings"][KidneySide]>,
  ) => {
    onChange({
      ...data,
      findings: {
        ...data.findings,
        [side]: { ...kidney, ...patch },
      },
    });
  };

  return (
    <WorksheetSection label={label}>
      <FindingLabel>Size</FindingLabel>
      <NumericInput
        value={kidney.sizeLengthCm}
        onChange={(v) => updateKidney({ sizeLengthCm: v })}
        unit="cm"
        label="Size"
        disabled={disabled}
      />

      <FindingLabel>Hydronephrosis</FindingLabel>
      <EnumChipSelect
        options={[...HYDRONEPHROSIS_OPTIONS]}
        selected={kidney.hydronephrosis}
        onChange={(v) =>
          updateKidney({
            hydronephrosis:
              v as RenalWorksheetData["findings"][KidneySide]["hydronephrosis"],
          })
        }
        disabled={disabled}
      />

      <FindingLabel>Stones</FindingLabel>
      <BooleanToggle
        value={kidney.stones}
        onChange={(v) => updateKidney({ stones: v })}
        disabled={disabled}
      />

      <FindingLabel>Perinephric Fluid</FindingLabel>
      <BooleanToggle
        value={kidney.perinephricFluid}
        onChange={(v) => updateKidney({ perinephricFluid: v })}
        disabled={disabled}
      />

      <FindingLabel>Cyst or Mass</FindingLabel>
      <BooleanToggle
        value={kidney.cystOrMass}
        onChange={(v) => updateKidney({ cystOrMass: v })}
        disabled={disabled}
      />
    </WorksheetSection>
  );
};

export const RenalWorksheet: FC<{
  data: RenalWorksheetData;
  onChange: (data: RenalWorksheetData) => void;
  isAllNormal: boolean;
  onToggleAllNormal: () => void;
  readOnly: boolean;
  imageGallery?: React.ReactNode;
}> = ({
  data,
  onChange,
  isAllNormal,
  onToggleAllNormal,
  readOnly,
  imageGallery,
}) => {
  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <AllNormalToggle
        isAllNormal={isAllNormal}
        onToggle={onToggleAllNormal}
        disabled={readOnly}
      />

      <WorksheetColumns>
        <WorksheetColumnLeft>
          <WorksheetSection label="Indication">
            <IndicationChips
              options={[...INDICATION_OPTIONS]}
              selected={data.indication}
              onChange={(v) =>
                onChange({
                  ...data,
                  indication: v as RenalWorksheetData["indication"],
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Views">
            <Typography
              style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
            >
              Tap to toggle obtained status
            </Typography>
            <ViewChecklist
              views={[...VIEW_OPTIONS]}
              checked={data.views}
              onChange={(key, checked) =>
                onChange({
                  ...data,
                  views: { ...data.views, [key]: checked },
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnLeft>

        <WorksheetColumnRight>
          <KidneySection
            label="Right Kidney"
            side="rKidney"
            data={data}
            onChange={onChange}
            disabled={readOnly}
          />

          <KidneySection
            label="Left Kidney"
            side="lKidney"
            data={data}
            onChange={onChange}
            disabled={readOnly}
          />

          <WorksheetSection label="Bladder">
            <FindingLabel>Assessed</FindingLabel>
            <BooleanToggle
              value={data.findings.bladderAssessed}
              onChange={(v) =>
                onChange({
                  ...data,
                  findings: { ...data.findings, bladderAssessed: v },
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          {imageGallery && (
            <WorksheetSection label="Images / Clips">
              {imageGallery}
            </WorksheetSection>
          )}

          <WorksheetSection label="Notes">
            <NotesField
              value={data.notes}
              onChange={(v) => onChange({ ...data, notes: v })}
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnRight>
      </WorksheetColumns>
    </Box>
  );
};
